import { FC, FormEvent } from 'react';
import { formOptions } from '@tanstack/react-form';
import { useAppForm } from '@new/common/hooks/form';
import { FormRow } from '@new/common/ui/FormRow/FormRow';
import { Button } from '@new/common/ui';
import { PERSONAL_NAME_ROW } from '../../constants';
import { User, useUpdatePersonalInfoMutation } from '@teachur/http-client';
import { toast } from 'react-toastify';

interface PersonalInfoFormProps {
  user: User;
  onCancel: () => void;
}

const personalInfoFormOpts = formOptions({
  defaultValues: {
    firstName: '',
    lastName: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: '',
  },
});

export const PersonalInfoForm: FC<PersonalInfoFormProps> = ({ user, onCancel }) => {
  const updatePersonalInfo = useUpdatePersonalInfoMutation(user.id);

  const form = useAppForm({
    ...personalInfoFormOpts,
    defaultValues: {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      phone: user.phone || '',
      address: user.address || '',
      city: user.city || '',
      state: user.state || '',
      zip: user.zip || '',
      country: user.country || '',
    },
    onSubmit: async ({ value }) => {
      const personalInfo = {
        firstName: value.firstName,
        lastName: value.lastName,
        phone: value.phone,
        address: value.address,
        city: value.city,
        state: value.state,
        zip: value.zip,
        country: value.country,
        email: user.email,
      };
      updatePersonalInfo.mutate(personalInfo as any);
      toast.success('Personal info updated successfully.');
      onCancel();
      return;
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    form.handleSubmit();
  };

  return (
    <form onSubmit={onSubmit} className="flex flex-col gap-5">
      <FormRow>
        {PERSONAL_NAME_ROW.map(({ name, label }) => (
          <form.AppField
            key={name}
            name={name}
            children={(field) => <field.Text label={label} placeholder={label} disabled />}
          />
        ))}
      </FormRow>

      <FormRow>
        <form.AppField
          name="phone"
          children={(field) => <field.Text label="Phone Number" placeholder="Phone Number" />}
        />
      </FormRow>

      <FormRow>
        <form.AppField
          name="address"
          children={(field) => <field.Text label="Street Address" placeholder="Street Address" />}
        />
      </FormRow>

      <FormRow>
        <form.AppField
          name="city"
          children={(field) => <field.Text label="City" placeholder="City" />}
        />
        <form.AppField
          name="state"
          children={(field) => <field.Text label="State/Region" placeholder="State/Region" />}
        />
      </FormRow>

      <FormRow>
        <form.AppField
          name="zip"
          children={(field) => <field.Text label="Zip Code" placeholder="Zip Code" />}
        />
        <form.AppField
          name="country"
          children={(field) => <field.Text label="Country" placeholder="Country" />}
        />
      </FormRow>

      <div className="flex flex-wrap gap-3">
        <form.AppForm>
          <form.SubmitButton label="Save changes" className="w-full sm:w-fit! px-8!" />
        </form.AppForm>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          className="w-full sm:w-fit! px-8!"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};
